<template>
  <div class="iot-dashboard">
    <main class="dashboard-main">
      <div class="main-inner">
        <el-row v-if="!customerScope" class="top-bar-row">
          <div class="top-bar stat-card card-shadow" style="padding:5px 5px 5px 22px">
            <div class="customer-select-group">
              <span class="customer-label">{{ $t('public.table_scope') }}:</span>
              <el-select
                v-model="selectCustomer"
                :placeholder="$t('public.select_scope')"
                class="customer-select"
                @change="handleCustomerChange"
              >
                <el-option
                  v-for="item in customerList"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id">
                </el-option>
              </el-select>
            </div>
            <div class="map-legend">
              <div class="legend-item">
                <span class="legend-dot dot-green"></span>
                <span>{{ $t('public.online') }}</span>
              </div>
              <div class="legend-item">
                <span class="legend-dot dot-yellow"></span>
                <span>{{ $t('integration.abnormal') }}</span>
              </div>
              <div class="legend-item">
                <span class="legend-dot dot-red"></span>
                <span>{{ $t('public.offline') }}</span>
              </div>
            </div>
          </div>
        </el-row>

        <!-- Top Stats Cards -->
        <el-row :gutter="16" class="stats-row">
          <el-col
            v-for="card in statsCards"
            :key="card.key"
            :xs="12"
            :sm="12"
            :md="6"
          >
            <div class="stat-card card-shadow">
              <div class="stat-card__head">
                <div class="stat-card__info">
                  <p class="stat-card__label">{{ $t('home.dashboard.' + card.i18nKey) }}</p>
                  <p
                    class="stat-card__value"
                    :class="card.valueClass"
                  >
                    {{ card.value }}
                  </p>
                </div>
                <div
                  class="stat-card__icon"
                  :class="card.iconBg"
                >
                  <i :class="card.icon"></i>
                </div>
              </div>
            </div>
          </el-col>
        </el-row>

        <!-- Main Dashboard Grid -->
        <el-row :gutter="24" class="main-grid">
          <!-- Left Column: Map View -->
          <el-col :xs="24" :lg="16">
            <div class="map-card card-shadow">
              <div class="map-card__head">
                <div class="map-card__title-group">
                  <h2 class="map-card__title">{{ $t('home.dashboard.location_map') }}</h2>
                </div>
              </div>

              <!-- Map Area -->
              <div class="map-area" ref="mapArea" :style="{ height: mapAreaHeight }">
                <!-- Tree Panel (Float) -->
                <transition name="tree-panel-slide">
                  <div v-show="treePanelVisible" class="tree-panel">
                    <div class="tree-panel__header">
                      <i class="el-icon-close tree-panel__close" @click="treePanelVisible = false"></i>
                    </div>
                    <div class="tree-panel__content">
                      <SchemaTree ref="schemaTree" :scopeId="selectCustomer" @changeNode="handleTreeChange" />
                    </div>
                  </div>
                </transition>

                <!-- Toggle Tree Button -->
                <div
                  class="toggle-tree-btn"
                  @click="treePanelVisible = true"
                  v-show="!treePanelVisible"
                >
                  <i class="el-icon-arrow-right"></i>
                </div>

                <!-- Map Container -->
                <div class="map-panel">
                  <div ref="assetMapContainer" class="map-container"></div>

                  <!-- 客户无定位提示 -->
                  <transition name="location-tip-fade">
                    <div v-if="showLocationTip" class="map-location-tip" :class="{ 'map-location-tip--with-tree': treePanelVisible }">
                      <i class="el-icon-warning"></i>
                      <span>{{ $t('home.dashboard.customer_no_location_message') }}</span>
                    </div>
                  </transition>

                  <div v-if="mapError" class="map-error">
                    <p><i class="fa fa-exclamation-circle"></i> {{ mapError }}</p>
                  </div>
                  <div v-if="!mapLoaded && !mapError" class="map-loading">
                    <i class="fa fa-spinner fa-spin"></i>
                    <p>{{ $t('home.dashboard.loading_map') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </el-col>

          <!-- Right Column: Real-time Monitoring -->
          <el-col :xs="24" :lg="8">
            <div class="right-column" ref="rightColumn">
              <!-- Equipment Ratio Pie Charts -->
              <div class="panel-card card-shadow panel-card--flex">
                <h3 class="panel-card__title">{{ $t('home.dashboard.device_ratio') }}</h3>
                <div class="equipment-ratio-charts">
                  <div class="equipment-ratio-chart-item">
                    <h4 class="chart-title">{{ $t('home.dashboard.abnormal_ratio') }}</h4>
                    <div ref="abnormalRatioChart" class="equipment-ratio-chart"></div>
                  </div>
                  <div class="equipment-ratio-chart-item">
                    <h4 class="chart-title">{{ $t('home.dashboard.offline_ratio') }}</h4>
                    <div ref="offlineRatioChart" class="equipment-ratio-chart"></div>
                  </div>
                </div>
              </div>

              <!-- Device Status -->
              <div class="panel-card card-shadow panel-card--flex">
                <h3 class="panel-card__title">{{ $t('home.dashboard.device_status') }}</h3>
                <div class="device-status-list">
                  <div
                    v-for="status in deviceStatus"
                    :key="status.key"
                    class="device-status-item"
                    :class="status.bgClass"
                  >
                    <div class="device-status__left">
                      <span class="status-dot" :class="status.dotClass"></span>
                      <span class="device-status__label">{{ $t('home.dashboard.' + status.i18nKey) }}</span>
                    </div>
                    <span class="device-status__count" :class="status.countClass">
                      {{ status.count }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Recent Alerts -->
              <div class="panel-card card-shadow">
                <div class="panel-card__head">
                  <h3 class="panel-card__title">{{ $t('home.dashboard.recent_events') }}</h3>
                  <a
                    href="javascript:void(0)"
                    class="panel-card__link"
                    @click="handleViewAllAlerts"
                  >
                    {{ $t('home.dashboard.view_all') }}
                  </a>
                </div>
                <div class="alerts-list">
                  <template v-if="recentAlerts.length > 0">
                    <div
                      v-for="alert in recentAlerts"
                      :key="alert.id"
                      class="alert-item"
                      :class="alert.borderClass"
                    >
                      <div class="alert-item__head">
                        <span class="alert-item__title">{{ alert.titleI18nKey ? $t('home.dashboard.' + alert.titleI18nKey) : '' }}</span>
                        <span class="alert-item__time">{{ formatAlertTime(alert.time) }}</span>
                      </div>
                      <p class="alert-item__desc">{{ alert.carrierName + ' ' + (alert.descI18nKey ? $t('home.dashboard.' + alert.descI18nKey) : '') }}</p>
                    </div>
                  </template>
                  <el-empty v-else :description="$t('integration.no_data_temporarily')" :image-size="80" style="min-height: 180px; display: flex; align-items: center; justify-content: center;"></el-empty>
                </div>
              </div>
            </div>
          </el-col>
        </el-row>

        <!-- Bottom Section: Trends -->
        <div class="trends-card card-shadow">
          <div class="trends-card__head">
            <h2 class="trends-card__title">{{ $t('home.dashboard.environmental_trends') }}</h2>
            <div class="trends-card__actions">
              <el-select
                v-model="trendRange"
                size="small"
                class="trends-card__select"
              >
                <el-option
                  v-for="opt in trendRangeOptions"
                  :key="opt"
                  :label="$t('home.dashboard.' + opt)"
                  :value="opt"
                />
              </el-select>
              <el-button
                circle
                size="small"
                icon="el-icon-refresh"
                class="trends-card__refresh"
                @click="handleRefreshTrends"
              />
            </div>
          </div>

          <el-row :gutter="24" :class="{ 'trend-row--stacked': isTrendStacked }">
            <!-- Temperature Trend -->
            <el-col :xs="24" :lg="isTrendStacked ? 24 : 12">
              <div class="trend-block">
                <h3 class="trend-block__title">{{ $t('home.dashboard.temperature_distribution') }}</h3>
                <div class="chart-bars">
                  <div
                    v-for="day in weeklyData"
                    :key="day.day"
                    class="chart-bar-col"
                  >
                    <div class="chart-bar-group">
                      <div
                        v-for="(item, idx) in day.tempItems"
                        :key="idx"
                        class="chart-bar chart-bar--grouped"
                        :class="item.class"
                        :style="{ height: item.value + '%' }"
                        @mouseenter="showTooltip($event, day.day, item.value, getStatusLabel(idx))"
                        @mouseleave="hideTooltip"
                      ></div>
                    </div>
                    <span class="chart-bar__label">{{ day.day }}</span>
                  </div>
                </div>
              </div>
            </el-col>

            <!-- Humidity Trend -->
            <el-col :xs="24" :lg="isTrendStacked ? 24 : 12">
              <div class="trend-block">
                <h3 class="trend-block__title">{{ $t('home.dashboard.humidity_distribution') }}</h3>
                <div class="chart-bars">
                  <div
                    v-for="day in weeklyData"
                    :key="day.day"
                    class="chart-bar-col"
                  >
                    <div class="chart-bar-group">
                      <div
                        v-for="(item, idx) in day.humidityItems"
                        :key="idx"
                        class="chart-bar chart-bar--grouped"
                        :class="item.class"
                        :style="{ height: item.value + '%' }"
                        @mouseenter="showTooltip($event, day.day, item.value, getStatusLabel(idx))"
                        @mouseleave="hideTooltip"
                      ></div>
                    </div>
                    <span class="chart-bar__label">{{ day.day }}</span>
                  </div>
                </div>
              </div>
            </el-col>
          </el-row>

          <div class="chart-legend">
            <div
              v-for="leg in trendLegend"
              :key="leg.key"
              class="chart-legend__item"
            >
              <span class="chart-legend__dot" :class="leg.dotClass"></span>
              <span class="chart-legend__text">{{ $t('home.dashboard.' + leg.i18nKey) }}</span>
            </div>
          </div>

          <div
            v-if="tooltipVisible"
            class="chart-tooltip"
            :style="{ left: tooltipX + 'px', top: tooltipY + 'px' }"
          >
            <div class="chart-tooltip__day">{{ tooltipDay }}</div>
            <div class="chart-tooltip__content">
              <span class="chart-tooltip__status">{{ tooltipStatus }}</span>
              <span class="chart-tooltip__value">{{ tooltipValue }}%</span>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 轨迹弹窗 -->
    <TrackDialog
      :visible.sync="trackDialogVisible"
      :carrier-id="trackCarrierId"
      :carrier-name="trackCarrierName"
    />
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import MapboxLanguage from '@mapbox/mapbox-gl-language'
import {
  getAssetStatistics, getRecentAlerts, getEnvironmentalTrends, getDeviceStatus, getCustomerPosition,
  getCustomerCenter
} from '@/api/modules/home.js'
import { goNewWindows } from '@/utils/goNewWin'
import bus from '@/utils/bus'
import SchemaTree from './components/treeContent.vue'
import TrackDialog from './components/TrackDialog.vue'
import getRange from '@/utils/getRange.js'
import { mapTrackPosition } from '@/api/modules/map'
import { TempUtils } from '@/utils/TempUtil.js'
import { sensorTypeConvert } from '@/utils/dict/sensor'

export default {
  mixins: [getRange],
  name: 'IotDashboard',
  components: {
    SchemaTree,
    TrackDialog
  },
  data() {
    return {
      sensorTypeConvert,
      customerList: [], // 客户选择list
      selectCustomer: null,
      currentTime: '',
      hoveredMarker: null,
      trendRange: 'trend_range_7d',
      mapInstance: null,
      mapMarkersData: [],
      mapLoaded: false,
      mapError: null,
      customerPosition: null,
      showLocationTip: false, // 是否显示无定位提示
      customerMarkers: [],
      sceneMarkers: [],
      sceneMapList: [],
      assetMarkers: [],
      assetMarkerElements: [], // 资产标记元素引用
      sceneMarkerElements: [], // 场景标记元素引用
      customerMarkerElement: null, // 客户标记元素引用
      traceAssetList: [],
      treePanelVisible: true,
      assetPanelLoading: false,
      assetList: [],
      selectedMarker: null,
      selectedMarkerElement: null, // 当前选中的地图标记元素
      openPopup: false,
      popupData: null,
      mapAreaHeight: '785px', // 地图区域高度，动态等于右侧列高度
      rightColumnResizeObserver: null, // 右侧列高度监听器
      trendRangeOptions: ['trend_range_7d', 'trend_range_15d', 'trend_range_30d'],
      statsCards: [
        {
          key: 'assets',
          label: '',
          value: '128',
          valueClass: '',
          icon: 'fa fa-server',
          iconBg: 'icon-bg-blue',
          i18nKey: 'total_assets'
        },
        {
          key: 'devices',
          label: '',
          value: '96',
          valueClass: 'text-green',
          icon: 'fa fa-building',
          iconBg: 'icon-bg-green',
          i18nKey: 'static_assets'
        },
        {
          key: 'exceptions',
          label: '',
          value: '7',
          valueClass: 'text-orange',
          icon: 'fa fa-truck',
          iconBg: 'icon-bg-orange',
          i18nKey: 'dynamic_assets_label'
        },
        {
          key: 'alarms',
          label: '',
          value: '12',
          valueClass: 'text-red',
          icon: 'fa fa-bell',
          iconBg: 'icon-bg-red',
          i18nKey: 'total_alarms'
        }
      ],
      mapLegend: [
        { key: 'active', dotClass: 'dot-green', label: 'Active (86)', bounce: true },
        { key: 'warning', dotClass: 'dot-yellow', label: 'Warning (24)', bounce: false },
        { key: 'offline', dotClass: 'dot-red', label: 'Offline (18)', bounce: false }
      ],
      mapMarkers: [

      ],
      abnormalRatioData: [
        { name: 'Normal', value: 86, itemStyle: { color: '#10b981' } },
        { name: 'Abnormal', value: 24, itemStyle: { color: '#f5a623' } }
      ],
      offlineRatioData: [
        { name: 'Normal', value: 86, itemStyle: { color: '#10b981' } },
        { name: 'Offline', value: 18, itemStyle: { color: '#e74c3c' } }
      ],
      deviceStatus: [
        {
          key: 'online',
          label: '',
          count: '86',
          dotClass: 'dot-green',
          countClass: 'text-green',
          bgClass: 'bg-green-50',
          i18nKey: 'online'
        },
        {
          key: 'abnormal',
          label: '',
          count: '24',
          dotClass: 'dot-orange',
          countClass: 'text-orange',
          bgClass: 'bg-orange-50',
          i18nKey: 'abnormal'
        },
        {
          key: 'offline',
          label: '',
          count: '18',
          dotClass: 'dot-red',
          countClass: 'text-red',
          bgClass: 'bg-red-50',
          i18nKey: 'offline'
        }
      ],
      recentAlerts: [
      ],
      trendLegend: [
        { key: 'normal', dotClass: 'dot-blue', label: '', i18nKey: 'normal' },
        { key: 'abnormal', dotClass: 'dot-yellow', label: '', i18nKey: 'abnormal' },
        { key: 'offline', dotClass: 'dot-red', label: '', i18nKey: 'offline' }
      ],
      weeklyData: [
        {
          day: 'Mon',
          tempItems: [
            { value: 65, class: 'bar-blue' },
            { value: 25, class: 'bar-yellow' },
            { value: 10, class: 'bar-red' }
          ],
          humidityItems: [
            { value: 55, class: 'bar-cyan' },
            { value: 35, class: 'bar-yellow' },
            { value: 10, class: 'bar-red' }
          ]
        },
        {
          day: 'Tue',
          tempItems: [
            { value: 70, class: 'bar-blue' },
            { value: 20, class: 'bar-yellow' },
            { value: 10, class: 'bar-red' }
          ],
          humidityItems: [
            { value: 60, class: 'bar-cyan' },
            { value: 30, class: 'bar-yellow' },
            { value: 10, class: 'bar-red' }
          ]
        },
        {
          day: 'Wed',
          tempItems: [
            { value: 55, class: 'bar-blue' },
            { value: 30, class: 'bar-yellow' },
            { value: 15, class: 'bar-red' }
          ],
          humidityItems: [
            { value: 45, class: 'bar-cyan' },
            { value: 40, class: 'bar-yellow' },
            { value: 15, class: 'bar-red' }
          ]
        },
        {
          day: 'Thu',
          tempItems: [
            { value: 75, class: 'bar-blue' },
            { value: 20, class: 'bar-yellow' },
            { value: 5, class: 'bar-red' }
          ],
          humidityItems: [
            { value: 70, class: 'bar-cyan' },
            { value: 25, class: 'bar-yellow' },
            { value: 5, class: 'bar-red' }
          ]
        },
        {
          day: 'Fri',
          tempItems: [
            { value: 68, class: 'bar-blue' },
            { value: 22, class: 'bar-yellow' },
            { value: 10, class: 'bar-red' }
          ],
          humidityItems: [
            { value: 58, class: 'bar-cyan' },
            { value: 32, class: 'bar-yellow' },
            { value: 10, class: 'bar-red' }
          ]
        },
        {
          day: 'Sat',
          tempItems: [
            { value: 60, class: 'bar-blue' },
            { value: 28, class: 'bar-yellow' },
            { value: 12, class: 'bar-red' }
          ],
          humidityItems: [
            { value: 52, class: 'bar-cyan' },
            { value: 38, class: 'bar-yellow' },
            { value: 10, class: 'bar-red' }
          ]
        },
        {
          day: 'Sun',
          tempItems: [
            { value: 72, class: 'bar-blue' },
            { value: 18, class: 'bar-yellow' },
            { value: 10, class: 'bar-red' }
          ],
          humidityItems: [
            { value: 65, class: 'bar-cyan' },
            { value: 25, class: 'bar-yellow' },
            { value: 10, class: 'bar-red' }
          ]
        }
      ],
      tooltipVisible: false,
      tooltipX: 0,
      tooltipY: 0,
      tooltipDay: '',
      tooltipStatus: '',
      tooltipValue: 0,
      refreshTimer: null, // 定时刷新计时器
      // 轨迹弹窗相关
      trackDialogVisible: false,
      trackCarrierId: null,
      trackCarrierName: ''
    };
  },
  mounted() {
    this.updateTime();
    setInterval(() => this.updateTime(), 1000);
    this.loadDashboardData(this.selectCustomer);
    this.$nextTick(() => {
      this.initAbnormalRatioChart();
      this.initOfflineRatioChart();
      this.loadCustomerPositionAndInitMap();
      this.initRightColumnHeightObserver();
    });
    // 启动定时刷新（5分钟）
    this.startRefreshTimer();
    // 监听菜单栏收缩事件，调整地图大小
    bus.$on('collapse', this.handleMenuCollapse);
  },
  created(){
    if(!this.customerScope && !this.isProxy){ // 平台角色
       this.getCustomer()
    }
    if(!this.customerScope && this.isProxy){ // 代理角色
      this.customerList = this.$store.getters.userInfo.switchScope
    }
    this.selectCustomer = this.$store.getters.userInfo.scope.sceneId

  },
  beforeDestroy() {
    // 清除定时刷新计时器
    this.stopRefreshTimer();
    // 移除菜单栏收缩事件监听
    bus.$off('collapse', this.handleMenuCollapse);
    // 清除右侧列高度监听器
    if (this.rightColumnResizeObserver) {
      this.rightColumnResizeObserver.disconnect();
      this.rightColumnResizeObserver = null;
    }
    if (this.abnormalRatioResizeHandler) {
      window.removeEventListener('resize', this.abnormalRatioResizeHandler);
      this.abnormalRatioResizeHandler = null;
    }
    if (this.abnormalRatioChart) {
      this.abnormalRatioChart.dispose();
      this.abnormalRatioChart = null;
    }
    if (this.offlineRatioResizeHandler) {
      window.removeEventListener('resize', this.offlineRatioResizeHandler);
      this.offlineRatioResizeHandler = null;
    }
    if (this.offlineRatioChart) {
      this.offlineRatioChart.dispose();
      this.offlineRatioChart = null;
    }
    this.cleanupMap();
  },
  methods: {
    // 初始化右侧列高度监听器，同步地图区域高度
    initRightColumnHeightObserver() {
      const rightColumn = this.$refs.rightColumn
      if (!rightColumn) return

      // 初始设置高度
      this.updateMapAreaHeight()

      // 使用 ResizeObserver 监听右侧列高度变化
      this.rightColumnResizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          if (entry.contentRect.height > 0) {
            this.updateMapAreaHeight()
          }
        }
      })
      this.rightColumnResizeObserver.observe(rightColumn)
    },
    // 更新地图区域高度
    updateMapAreaHeight() {
      const rightColumn = this.$refs.rightColumn
      const mapCard = this.$el.querySelector('.map-card')
      if (rightColumn && mapCard) {
        // 直接使用右侧列的总高度（已包含 gap）
        const totalHeight = rightColumn.offsetHeight

        // 获取地图卡片的实际内边距
        const mapCardStyle = getComputedStyle(mapCard)
        const mapCardPaddingTop = parseFloat(mapCardStyle.paddingTop) || 0
        const mapCardPaddingBottom = parseFloat(mapCardStyle.paddingBottom) || 0

        // 获取地图标题区域的实际高度（包括 margin）
        const mapCardHead = mapCard.querySelector('.map-card__head')
        let mapTitleHeight = 0
        if (mapCardHead) {
          const headStyle = getComputedStyle(mapCardHead)
          const marginBottom = parseFloat(headStyle.marginBottom) || 0
          mapTitleHeight = mapCardHead.offsetHeight + marginBottom
        }

        // 地图区域高度 = 右侧列总高度 - 地图卡片的上下内边距 - 地图标题高度
        const calculatedHeight = totalHeight - mapCardPaddingTop - mapCardPaddingBottom - mapTitleHeight
        console.log('rightColumn.offsetHeight:', totalHeight, 'paddingTop:', mapCardPaddingTop, 'paddingBottom:', mapCardPaddingBottom, 'titleHeight:', mapTitleHeight, 'result:', calculatedHeight)
        this.mapAreaHeight = calculatedHeight + 'px'

        // 同步更新地图尺寸
        if (this.mapInstance) {
          this.$nextTick(() => {
            this.mapInstance.resize()
          })
        }
      }
    },
    // 处理菜单栏收缩事件，调整地图大小
    handleMenuCollapse () {
      // 延迟执行，等待 CSS 过渡完成（过渡时间 500ms）
      setTimeout(() => {
        if (this.mapInstance) {
          // 强制重新计算容器尺寸
          const container = this.$refs.assetMapContainer
          if (container) {
            // 触发重排
            container.style.width = '100%'
          }
          this.mapInstance.resize()
        }
      }, 550)
    },
    // 启动定时刷新
    startRefreshTimer () {
      // 每5分钟刷新一次数据（300000毫秒）
      this.refreshTimer = setInterval(() => {
        this.refreshAllData()
      }, 300000)
    },
    // 停止定时刷新
    stopRefreshTimer () {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },
    // 刷新所有数据
    refreshAllData () {
      this.loadDashboardData(this.selectCustomer)
      // 只刷新地图标记点，不重新初始化地图
      this.loadMapMarker(this.selectCustomer, this.selectCustomer)
    },
    async getCustomer () {
      const { result } = await this.getCustomerList({
        type: 1,
        tab: 1
      })
      let list = []
      result.forEach((item, index) => {
        if (item.id !== '-1' && item.id !== '-2') {
          list.push(item)
        }
      })
      this.customerList = list
      // 默认选择第一个客户组织
      if (this.customerList.length > 0) {
        this.selectCustomer = this.customerList[0].id
      }
    },
    getStatusLabel (idx) {
      const keys = ['normal', 'abnormal', 'offline'];
      return this.$t('home.dashboard.' + keys[idx]) || '';
    },
    showTooltip (event, day, value, status) {
      this.tooltipVisible = true;
      this.tooltipX = event.clientX + 10;
      this.tooltipY = event.clientY - 50;
      this.tooltipDay = day;
      this.tooltipStatus = status;
      this.tooltipValue = value;
    },
    hideTooltip () {
      this.tooltipVisible = false;
    },
    async loadCustomerPositionAndInitMap() {
      try {
        const res = await getCustomerCenter();
        if (res && res.result) {
          const data = res.result;
          if (data.lng != null && data.lat != null && !isNaN(data.lng) && !isNaN(data.lat)) {
            this.customerPosition = data;
          }
        }
      } catch (error) {
        console.error('Failed to load customer position:', error);
      }

      // 如果没有客户定位，在地图区域显示提示，然后尝试获取用户当前位置
      if (!this.customerPosition) {
        this.showLocationTip = true;
        // 5秒后自动关闭提示并继续定位流程
        setTimeout(() => {
          this.showLocationTip = false;
          this.locateBrowserAndInitMap();
        }, 5000);
      } else {
        this.initMap();
      }
    },
    // 获取用户浏览器当前位置
    locateBrowserAndInitMap() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const coords = {
              lng: position.coords.longitude,
              lat: position.coords.latitude,
              name: this.customerPosition?.name || this.$t('home.dashboard.current_location') || '当前位置'
            };
            this.customerPosition = coords;
            this.initMap();
          },
          error => {
            console.warn('浏览器定位失败，使用默认位置', error);
            this.applyDefaultLocation();
          }
        );
      } else {
        console.warn('浏览器不支持地理定位，使用默认位置');
        this.applyDefaultLocation();
      }
    },
    // 应用默认位置
    applyDefaultLocation() {
      const defaultLng = -123.1192;
      const defaultLat = 49.2638;
      this.customerPosition = {
        lng: defaultLng,
        lat: defaultLat,
        name: this.$t('home.dashboard.default_location') || '默认位置'
      };
      this.initMap();
    },
    initAbnormalRatioChart () {
      this.abnormalRatioChart = this.$echarts.init(this.$refs.abnormalRatioChart);
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'horizontal',
          bottom: '-5%',
          left: 'center'
        },
        series: [
          {
            name: 'Abnormal Ratio',
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 4,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: true,
              position: 'outside',
              formatter: function(params) {
                return params.percent.toFixed(2) + '%'
              }
            },
            labelLine: {
              show: true,
              length: 5,
              length2: 5
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 14,
                fontWeight: 'bold'
              }
            },
            data: this.abnormalRatioData
          }
        ]
      };
      this.abnormalRatioChart.setOption(option);
      this.abnormalRatioResizeHandler = () => {
        this.abnormalRatioChart && this.abnormalRatioChart.resize();
      };
      window.addEventListener('resize', this.abnormalRatioResizeHandler);
    },
    initOfflineRatioChart () {
      this.offlineRatioChart = this.$echarts.init(this.$refs.offlineRatioChart);
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'horizontal',
          bottom: '-5%',
          left: 'center'
        },
        series: [
          {
            name: 'Offline Ratio',
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 4,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: true,
              position: 'outside',
              formatter: function(params) {
                return params.percent.toFixed(2) + '%'
              }
            },
            labelLine: {
              show: true,
              length: 5,
              length2: 5
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 14,
                fontWeight: 'bold'
              }
            },
            data: this.offlineRatioData
          }
        ]
      };
      this.offlineRatioChart.setOption(option);
      this.offlineRatioResizeHandler = () => {
        this.offlineRatioChart && this.offlineRatioChart.resize();
      };
      window.addEventListener('resize', this.offlineRatioResizeHandler);
    },
    initMap (retryCount = 0) {
      const token = process.env.VUE_APP_MAPBOX_TOKEN;
      if (!token) {
        this.mapError = 'Mapbox token is not configured';
        return;
      }

      if (!this.$refs.assetMapContainer) {
        if (retryCount < 10) {
          setTimeout(() => this.initMap(retryCount + 1), 300);
        } else {
          this.mapError = 'Map container not found';
        }
        return;
      }

      const container = this.$refs.assetMapContainer;
      const rect = container.getBoundingClientRect();
      console.log('Map container size:', rect.width, 'x', rect.height);

      if (rect.width === 0) {
        if (retryCount < 10) {
          setTimeout(() => this.initMap(retryCount + 1), 300);
        } else {
          this.mapError = 'Map container width is zero after retries';
        }
        return;
      }

      mapboxgl.accessToken = token;
      mapboxgl.telemetryEnabled = false;

      const language = this.getMapboxLanguage();

      const defaultCenter = [-123.1192, 49.2638];

      const mapCenter = this.customerPosition
        ? [this.customerPosition.lng ? this.customerPosition.lng : -123.1192 , this.customerPosition.lat ? this.customerPosition.lat : 49.2638  ]
        : defaultCenter;
      try {
        this.mapInstance = new mapboxgl.Map({
          container: container,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: mapCenter,
          zoom: 12,
          attributionControl: false,
        });

        this.mapInstance.addControl(new mapboxgl.NavigationControl());

        const languagePlugin = new MapboxLanguage({
          defaultLanguage: language
        });
        this.mapInstance.addControl(languagePlugin);

        this.mapInstance.on('load', () => {
          this.mapLoaded = true;
          this.mapError = null;
          this.addCustomerMarker();
          // 地图加载完成后，加载场景和资产标记
          if (this.selectCustomer) {
            this.loadMapMarker(this.selectCustomer, this.selectCustomer);
          }
        });

        this.mapInstance.on('error', (e) => {
          this.mapError = 'Mapbox loading error: ' + e.message;
        });
      } catch (err) {
        this.mapError = 'Failed to initialize Mapbox: ' + err.message;
      }
    },
    cleanupMap () {
      this.clearCustomerMarkers();
      if (this.mapInstance) {
        this.mapInstance.remove();
        this.mapInstance = null;
      }
    },
    getMapboxLanguage () {
      const lang = this.$i18n.locale || 'zh_CN';
      console.log('lang = ', lang);
      const langMap = {
        'zh_CN': 'zh-Hans',
        'en_US': 'en',
        'es': 'es',
        'ja': 'ja'
      };
      return langMap[lang] || 'en';
    },
    updateMapLanguage () {
      if (!this.mapInstance || !this.mapLoaded) return;
      const language = this.getMapboxLanguage();
      const style = this.mapInstance.getStyle();
      if (!style || !style.layers) return;
      style.layers.forEach(layer => {
        if (layer.type === 'symbol' && layer.layout && layer.layout['text-field']) {
          this.mapInstance.setLayoutProperty(layer.id, 'text-field', ['coalesce', ['get', 'name_' + language], ['get', 'name']]);
        }
      });
    },
    handleViewAllAlerts () {
      this.$router.push('/invalidRule');
    },
    handleTreeChange (row) {
      // 设置选中标记的通用方法
      const setSelectedMarker = (element) => {
        if (this.selectedMarkerElement) {
          this.selectedMarkerElement.classList.remove('marker-selected');
        }
        if (element) {
          element.classList.add('marker-selected');
          this.selectedMarkerElement = element;
        }
      };

      if (row.nodeType !== 0 && row.nodeType !== 1) {
        const sceneId = row.level === 0 ? row.scopeId : row.sceneId
        if (row.funcType === 0 ){
          // 客户节点 - 选中客户标记
          const lng = parseFloat(this.customerPosition.lng);
          const lat = parseFloat(this.customerPosition.lat);
          if (!isNaN(lng) && !isNaN(lat) && lng !== 0 && lat !== 0) {
            this.handleCenter(lng, lat);
          }
          setSelectedMarker(this.customerMarkerElement);
        }else {
          // 场景节点 - 查找对应的场景标记
          this.sceneMapList.forEach(scene => {
            if (scene.id === sceneId) {
              const lng = parseFloat(scene.longitude)
              const lat = parseFloat(scene.latitude)
              if (!isNaN(lng) && !isNaN(lat) && lng !== 0 && lat !== 0) {
                this.handleCenter(lng, lat)
              }
              // 查找并选中对应的场景标记元素
              const markerInfo = this.sceneMarkerElements.find(m => m.id === sceneId);
              if (markerInfo) {
                setSelectedMarker(markerInfo.element);
              }
            }
          })
        }
      } else if (row.nodeType === 1) {
        // 资产节点 - 查找对应的资产标记
        this.assetList.forEach(asset => {
          if (asset.id === row.carrierId || asset.carrierId === row.carrierId) {
            const lng = parseFloat(asset.lng);
            const lat = parseFloat(asset.lat);
            if (!isNaN(lng) && !isNaN(lat) && lng !== 0 && lat !== 0) {
              this.handleCenter(lng, lat);
            }
            // 查找并选中对应的资产标记元素
            const markerInfo = this.assetMarkerElements.find(m => m.id === row.carrierId || m.id === asset.id);
            if (markerInfo) {
              setSelectedMarker(markerInfo.element);
              // 打开弹窗
              if (markerInfo.popup) {
                if (this.openPopup && this.popupData) {
                  this.popupData.remove()
                }
                this.openPopup = true
                this.popupData = markerInfo.popup
                markerInfo.popup.addTo(this.mapInstance)
              }
            }
          }
        })
      }
    },
    handleCenter(lng, lat){
      this.mapInstance.flyTo({
        center: [lng, lat],
        zoom: 14,
        duration: 1000
      });
    },
    // 点击地图标记时，选中对应的树节点
    handleMapMarkerClick (type, markerData, element) {
      let nodeId = null;

      // 清除之前选中的标记样式
      if (this.selectedMarkerElement) {
        this.selectedMarkerElement.classList.remove('marker-selected');
      }

      // 设置当前选中的标记样式
      if (element) {
        element.classList.add('marker-selected');
        this.selectedMarkerElement = element;
      }

      // 设置地图中心点
      if (this.mapInstance) {
        let lng = null;
        let lat = null;
        if (type === 1) {
          // 场景标记
          lng = parseFloat(markerData.longitude);
          lat = parseFloat(markerData.latitude);
        } else if (type === 2) {
          // 资产标记
          lng = parseFloat(markerData.lng);
          lat = parseFloat(markerData.lat);
        }else if (type === 0) {
          // 资产标记
          lng = parseFloat(markerData.lng);
          lat = parseFloat(markerData.lat);
        }
        if (!isNaN(lng) && !isNaN(lat) && lng !== 0 && lat !== 0) {
          this.handleCenter(lng, lat);
        }
      }

      if (type === 0) {
        nodeId = markerData.id;
      } else {
        // 对于资产(type=2)，优先使用 carrierId，其次使用 id
        nodeId = markerData.carrierId || markerData.id;
      }

      if (nodeId && this.$refs.schemaTree) {
        this.$refs.schemaTree.selectNodeById(type, nodeId);
        // 如果树面板隐藏，则显示它
        if (!this.treePanelVisible) {
          this.treePanelVisible = true;
        }
      }
    },

    handleRefreshTrends () {
      this.loadEnvironmentalTrends();
    },

    addCustomerMarker () {
      this.clearCustomerMarkers();
      if (!this.customerPosition) return;

      const pos = this.customerPosition;
      const el = document.createElement('div');
      el.className = 'customer-map-marker marker-selected';
      el.dataset.markerId = this.scopeId || pos.id; // 添加数据属性用于查找
      this.selectedMarkerElement = el;
      this.customerMarkerElement = el;
      el.innerHTML = `
        <div class="customer-map-marker__dot">
          <i class="iconfont icon-zhuzhaiqu" style="font-size: 40px;"></i>
        </div>
        <div class="customer-map-marker__label">${this.customerPosition.name}</div>`;
      const marker = new mapboxgl.Marker(el)
        .setLngLat([pos.lng, pos.lat])
        .addTo(this.mapInstance);
      el.addEventListener('click', () => {
        this.handleMapMarkerClick(0, this.customerPosition, el);
      });

      this.customerMarkers.push(marker);
    },
    clearCustomerMarkers () {
      this.customerMarkers.forEach(marker => marker.remove());
      this.customerMarkers = [];
    },
    loadDashboardData (scopeId) {
      this.loadAssetStatistics(scopeId);
      this.loadRecentAlerts(scopeId);
      this.loadEnvironmentalTrends(scopeId);
      this.loadDeviceStatus(scopeId);
      this.loadMapMarker(scopeId, scopeId);
    },
    async loadAssetStatistics (scopeId) {
      try {
        const res = await getAssetStatistics({ scopeId: scopeId });
        if (res && res.result) {
          const data = res.result;
          if (this.statsCards[0]) this.statsCards[0].value = data.totalAssets || '0';
          if (this.statsCards[1]) this.statsCards[1].value = data.staticAssets || '0';
          if (this.statsCards[2]) this.statsCards[2].value = data.dynamicAssets || '0';
          if (this.statsCards[3]) this.statsCards[3].value = data.totalAlarms || '0';

        }
      } catch (error) {
        console.error('Failed to load asset statistics:', error);
      }
    },
    async loadDeviceStatus (scopeId) {
      try {
        const res = await getDeviceStatus({ scopeId: scopeId });
        if (res && res.result) {
          const data = res.result;
          if (this.deviceStatus[0]) this.deviceStatus[0].count = data.onlineCount > 0 ? data.onlineCount.toString() : '0';
          if (this.deviceStatus[1]) this.deviceStatus[1].count = data.abnormalCount > 0 ? data.abnormalCount.toString() : '0';
          if (this.deviceStatus[2]) this.deviceStatus[2].count = data.offlineCount > 0 ? data.offlineCount.toString() : '0';
          this.updateEquipmentRatioCharts(data.onlineCount, data.abnormalCount, data.offlineCount);
        }

      } catch (error) {
        console.error('Failed to load device status:', error);
      }
    },
    async loadRecentAlerts (scopeId) {
      try {
        const res = await getRecentAlerts({ scopeId: scopeId });
        if (res && res.result) {
          const alerts = res.result;
          this.recentAlerts = alerts.map((alert, index) => ({
            id: alert.id || index + 1,
            titleI18nKey: alert.exCatalog === 0 ? 'device_offline' :
              (alert.exCatalog === 2 ? 'device_power_outage' :
                (alert.exType === 2 ? 'temperature_alert' : alert.exType === 3 ? 'humidity_alert' : '')),
            time: alert.happenTime,
            descI18nKey: alert.exCatalog === 0 ? 'lost_connection' : alert.exCatalog === 1 ? 'exceeded_threshold' : 'power_outage',
            carrierName: alert.carrierName || '',
            borderClass: this.getAlertBorderClass(alert.exCatalog)
          }));
        }
      } catch (error) {
        console.error('Failed to load recent alerts:', error);
      }
    },
    async loadEnvironmentalTrends (scopeId) {
      try {
        const now = Date.now();
        let days = 7;
        if (this.trendRange === 'trend_range_15d') days = 15;
        else if (this.trendRange === 'trend_range_30d') days = 30;

        const from = now - days * 24 * 60 * 60 * 1000;

        const tempRes = await getEnvironmentalTrends({
          scopeId: scopeId,
          from,
          to: now,
          probe: 'temp'
        });
        const humRes = await getEnvironmentalTrends({
          scopeId: scopeId,
          from,
          to: now,
          probe: 'hum'
        });

        // 先生成完整日期骨架
        const skeleton = this.generateEmptyTrendData(days);

        if (tempRes && tempRes.result && tempRes.result.length > 0) {
          const tempData = this.formatTrendData(tempRes.result);
          // 将接口数据合并到骨架中
          this.mergeTrendDataToSkeleton(skeleton, tempData);
          if (humRes && humRes.result && humRes.result.length > 0) {
            this.mergeHumidityData(skeleton, humRes.result);
          }
        }

        this.weeklyData = skeleton;
      } catch (error) {
        console.error('Failed to load environmental trends:', error);
        let days = 7;
        if (this.trendRange === 'trend_range_15d') days = 15;
        else if (this.trendRange === 'trend_range_30d') days = 30;
        this.weeklyData = this.generateEmptyTrendData(days);
      }
    },
    async loadCustomerCenter (sceneId) {
      getCustomerCenter({
        sceneId: sceneId
      }).then(({ result }) => {
        this.customerPosition = result;
      })
      const defaultCenter = [-123.1192, 49.2638];
      const mapCenter = this.customerPosition
        ? [this.customerPosition.lng ? this.customerPosition.lng : -123.1192 , this.customerPosition.lat ? this.customerPosition.lat : 49.2638  ]
        : defaultCenter;
    },
    async loadMapMarker (scopeId, sceneId) {
      getCustomerPosition({
        sceneId: sceneId,
        scopeId: scopeId
      }).then(({ result }) => {
        if (result && result.scene) {
          this.sceneMapList = result.scene;
          this.loadSceneMapMarker(this.sceneMapList)
        }
        if (result && result.carrier) {
          this.assetList = result.carrier;
          this.loadAssetMarker(this.assetList)
        }
      })
    },
    loadSceneMapMarker (sceneMapList) {
      if (!this.mapInstance) return;
      this.clearMarkers(this.sceneMarkers)
      this.sceneMarkers = [];
      this.sceneMarkerElements = [];
      if (sceneMapList.length > 0) {
        sceneMapList.forEach(sceneData => {
          const itemLng = parseFloat(sceneData.longitude)
          const itemLat = parseFloat(sceneData.latitude)
          const sceneName = sceneData.name || sceneData.sceneName || ''
          if (!isNaN(itemLng) && !isNaN(itemLat) && itemLng !== 0 && itemLat !== 0) {
            const el = document.createElement('div')
            el.className = 'customer-map-marker'
            el.dataset.markerId = sceneData.id; // 添加数据属性用于查找
            el.dataset.markerType = 'scene'; // 标记类型
            el.innerHTML = `
<div class="customer-map-marker__dot">
  <i class="iconfont icon-wuliuzhongxin" style="font-size: 35px;"></i>
</div>
<div class="customer-map-marker__label">${sceneName}</div>`;
            el.style.cursor = 'pointer';
            // 点击地图标记时，选中对应的树节点
            el.addEventListener('click', () => {
              this.handleMapMarkerClick(1, sceneData, el);
            });
            const marker = new mapboxgl.Marker(el)
              .setLngLat([itemLng, itemLat])
              .addTo(this.mapInstance);
            this.sceneMarkers.push(marker)
            this.sceneMarkerElements.push({ id: sceneData.id, element: el });
          }

        })
      }
    },
    loadAssetMarker (traceAssetList) {
      if (!this.mapInstance) return;
      this.clearMarkers(this.assetMarkers)
      this.assetMarkers = [];
      this.assetMarkerElements = [];
      if (traceAssetList.length > 0) {
        traceAssetList.forEach(assetData => {
          const itemLng = parseFloat(assetData.lng)
          const itemLat = parseFloat(assetData.lat)
          const assetName = assetData.keycode ;
          if (!isNaN(itemLng) && !isNaN(itemLat) && itemLng !== 0 && itemLat !== 0) {
            const el = document.createElement('div')
            el.className = 'customer-map-marker'
            el.dataset.markerId = assetData.carrierId || assetData.id; // 添加数据属性用于查找
            el.dataset.markerType = 'asset'; // 标记类型
            el.innerHTML = `
<div class="customer-map-marker__dot">
  <i class="iconfont icon-a-11-xianghuozhuizong" style="font-size: 40px;"></i>
</div>
<div class="customer-map-marker__label">${assetName}</div>`;
            el.style.cursor = 'pointer';

            // 构建取样值HTML
            const sampleValueHtml = this.buildSampleValueHtml(assetData.sampleValue, assetData.carrierId || assetData.id)

            // 构建弹窗内容
            const popupContent = `
              <div class="popup-card">
                <div class="popup-header">
                  <div class="popup-icon" style="background:linear-gradient(135deg,#1e57e8,#ffffff);">
                    <i class="iconfont icon-a-11-xianghuozhuizong" style="font-size:18px;"></i>
                  </div>
                  <div class="popup-info">
                    <div class="popup-title">${assetName}</div>
                    <div class="popup-transport">${assetData.transport === 1 ? this.$t('map.moving') : ''}</div>
                  </div>
                </div>
                <div class="popup-stats">
                  <div class="popup-stat">
                    <span class="popup-stat-value">${assetData.lat ? assetData.lat.toFixed(4) : '--'}</span>
                    <span class="popup-stat-label">${this.$t('map.latitude')}</span>
                  </div>
                  <div class="popup-stat">
                    <span class="popup-stat-value">${assetData.lng ? assetData.lng.toFixed(4) : '--'}</span>
                    <span class="popup-stat-label">${this.$t('map.longitude')}</span>
                  </div>
                </div>
                ${sampleValueHtml}
                <div class="popup-actions">
                  ${assetData.transport === 1 ? `<button class="popup-btn track-btn" data-action="viewTrack" data-id="${assetData.carrierId || assetData.id}" data-name="${assetName}">${this.$t('map.view_track')}</button>` : ''}
                  <button class="popup-btn secondary" data-action="assectDetail" data-id="${assetData.id}" data-name="${assetName}" data-type="${assetData.typeId}">${this.$t('map.details')}</button>
                </div>
              </div>`

            const popup = new mapboxgl.Popup({ offset: [0, -35], className: 'custom-popup', anchor: 'bottom' })
              .setHTML(popupContent)
            popup.on('open', () => {
              this.bindPopupActions(popup, assetData)
            })

            const marker = new mapboxgl.Marker(el)
              .setLngLat([itemLng, itemLat])
              .setPopup(popup)
              .addTo(this.mapInstance);

            // 点击地图标记时，选中对应的树节点并显示弹窗
            el.addEventListener('click', (e) => {
              this.handleMapMarkerClick(2, assetData, el);
              e.stopPropagation()
              if (this.openPopup && this.popupData) {
                this.popupData.remove()
              }
              this.openPopup = true
              this.popupData = popup
              if (popup.isOpen()) {
                popup.remove()
              } else {
                popup.addTo(this.mapInstance)
              }
            })

            this.assetMarkers.push(marker)
            this.assetMarkerElements.push({ id: assetData.carrierId || assetData.id, element: el, popup: popup });
          }
        })
      }
    },
    clearMarkers (mapMarkersData) {
      if (!mapMarkersData || mapMarkersData.length === 0) return;
      mapMarkersData.forEach(marker => {
        if (marker && marker.remove) {
          marker.remove();
        }
      });
    },

    // 构建取样值HTML - 带轮播功能，参考 temperature-monitor.html 样式
    buildSampleValueHtml (sampleValue, markerId) {
      if (!sampleValue) return ''

      let data = sampleValue
      if (typeof sampleValue === 'string') {
        try {
          data = JSON.parse(sampleValue)
        } catch (e) {
          return ''
        }
      }

      const tempArr = data.temp || []
      const humArr = data.hum || []

      if (tempArr.length === 0 && humArr.length === 0) return ''

      // 合并温度和湿度数据
      const sampleItems = [
        ...humArr.map(item => ({ ...item, code: item.code || 'hum' })),
        ...tempArr.map(item => ({ ...item, code: item.code || 'temp' }))
      ]

      if (sampleItems.length === 0) return ''

      // 为每个取样项生成轮播内容
      let slidesHtml = ''

      sampleItems.forEach((item, index) => {
        const unit = this.getUnit(item.code)
        const label = item.code === 'temp' ? this.$t('map.temperature') : this.$t('map.humidity')
        const time = item.time ? this.formatSampleTime(item.time) : ''
        const rangeText = item.isRange === 0 && item.min !== undefined && item.max !== undefined
          ? `${this.formatTempValue(item.min,unit)}${this.getUnit(item.code)} ~ ${this.formatTempValue(item.max,unit)}${this.getUnit(item.code)}`
          : (item.code === 'temp' ? this.$t('integration.unrelated_temperature_range') : this.$t('integration.unrelated_humidity_range'))

        // 获取 itemMap 中的所有值
        let itemMapItems = []
        if (item.itemMap && item.itemMap.length > 0) {
          // 按时间排序
          itemMapItems = [...item.itemMap].sort((a, b) => b.time - a.time)
        }

        // 主值
        let mainValue = '--'

        if (itemMapItems.length > 0) {
          mainValue = this.formatTempValue(itemMapItems[0].val, unit)
        }


        // 构建单个滑块
        slidesHtml += `
          <div class="sample-slide" data-index="${index}" style="${index === 0 ? '' : 'display:none;'}">
            <div class="sample-header-bar">
              <i class="iconfont ${item.code === 'temp' ? 'icon-wenduji' : 'icon-wenshiduji'}"></i>
              <span class="sample-header-time">${time}</span>
            </div>
            <div class="sample-range-banner">${rangeText}</div>
            <div class="sample-main-display">
              <span class="sample-main-value">${mainValue}</span>
              <span class="sample-main-unit">${unit}</span>
            </div>
            <div class="sample-records">
              ${this.buildSampleItemListHtml(itemMapItems, item.code)}
            </div>
          </div>`
      })

      // 构建完整的轮播HTML
      const showNav = sampleItems.length > 1
      let html = `
        <div class="popup-sample-carousel">
          ${showNav ? '<button class="sample-nav-btn sample-nav-prev" data-action="prev-sample">‹</button>' : ''}
          <div class="sample-slides-wrapper">
            ${slidesHtml}
          </div>
          ${showNav ? '<button class="sample-nav-btn sample-nav-next" data-action="next-sample">›</button>' : ''}
        </div>`
      return html
    },

    // 构建取样值项目列表HTML
    buildSampleItemListHtml (itemMapItems, code) {
      if (!itemMapItems || itemMapItems.length === 0) {
        return '<div class="sample-empty">---</div>'
      }

      let html = ''
      itemMapItems.forEach(item => {
        const unit = this.getUnit(code)
        const value = this.formatTempValue(item.val, unit)
        const no = item.no || ''
        const type = item.type
        const time = item.time ? this.formatSampleTime(item.time) : '---'

        // 根据 type 获取图标
        const iconClass = type !== undefined ? this.sensorTypeConvert(type).icon : (code === 'temp' ? 'icon-wenduji' : 'icon-wenshiduji')

        html += `
          <div class="sample-record-item">
            <div class="sample-record-left">
              <div class="sample-device-icon">
                <i class="iconfont ${iconClass}"></i>
              </div>
              <div class="sample-device-info">
                <div class="sample-device-id">${no}</div>
                <div class="sample-device-time">${time}</div>
              </div>
            </div>
            <div class="sample-record-right">
              <span class="sample-temp-reading">${value} ${unit}</span>
            </div>
          </div>`
      })
      return html
    },

    // 格式化温度值
    formatTempValue (val, code) {
      if (val === null || val === undefined || val === '') return '--'
      const numVal = Number(val)
      if (isNaN(numVal)) return '--'

      const result = TempUtils.convertToCelsius(numVal, code)
      return `${result}`
    },

    // 格式化取样时间
    formatSampleTime (timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },

    // 获取单位
    getUnit (code) {
      const { unit } = this.$store.state
      if (unit && unit[code]) {
        return unit[code].name || ''
      }
      return code === 'temp' ? '°C' : '%RH'
    },

    // 绑定弹窗按钮事件
    bindPopupActions (popup, marker) {
      const popupEl = popup.getElement()
      const buttons = popupEl.querySelectorAll('.popup-btn')
      buttons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation()
          const action = btn.dataset.action
          const id = btn.dataset.id
          if (action === 'assectDetail') {
            const name = btn.dataset.name
            const type = btn.dataset.type
            goNewWindows({
              path: '/newCarrierDetail',
              way: '_blank',
              query: {
                keycode: name,
                type: type,
                id: id
              }
            })
          } else if (action === 'viewTrack') {
            const name = btn.dataset.name
            this.handleViewTrack(id, name)
          }
          popup.remove()
        })
      })

      // 绑定轮播按钮事件
      const navBtns = popupEl.querySelectorAll('.sample-nav-btn')
      navBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation()
          this.handleSampleNavClick(popupEl, btn.dataset.action)
        })
      })
    },

    // 处理取样值轮播导航
    handleSampleNavClick (popupEl, action) {
      const slides = popupEl.querySelectorAll('.sample-slide')
      if (slides.length <= 1) return

      // 找到当前显示的滑块
      let currentIndex = -1
      slides.forEach((slide, index) => {
        if (slide.style.display !== 'none' && currentIndex === -1) {
          currentIndex = index
        }
      })
      if (currentIndex === -1) currentIndex = 0

      // 计算新的索引
      let newIndex = currentIndex
      if (action === 'prev-sample') {
        newIndex = currentIndex > 0 ? currentIndex - 1 : slides.length - 1
      } else if (action === 'next-sample') {
        newIndex = currentIndex < slides.length - 1 ? currentIndex + 1 : 0
      }

      // 隐藏所有滑块
      slides.forEach(slide => {
        slide.style.display = 'none'
      })

      // 显示新的滑块
      slides[newIndex].style.display = 'block'
    },
    generateEmptyTrendData(days) {
      const result = [];
      const now = new Date();
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const MM = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        result.push({
          day: MM + '-' + dd,
          dateStr: MM + '-' + dd,
          tempItems: [
            { value: 0, class: 'bar-blue' },
            { value: 0, class: 'bar-yellow' },
            { value: 0, class: 'bar-red' }
          ],
          humidityItems: [
            { value: 0, class: 'bar-cyan' },
            { value: 0, class: 'bar-yellow' },
            { value: 0, class: 'bar-red' }
          ]
        });
      }
      return result;
    },
    mergeTrendDataToSkeleton(skeleton, trendData) {
      trendData.forEach(item => {
        const match = skeleton.find(s => s.day === item.day);
        if (match) {
          match.tempItems = item.tempItems;
          match.dateStr = item.dateStr;
        }
      });
    },
    formatTrendData(trendData) {
      const result = [];

      trendData.forEach(item => {
        const date = new Date(item.dateTime);
        const MM = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const dayLabel = MM + '-' + dd;
        const total = (item.normalCount || 0) + (item.abnormalCount || 0) + (item.offlineCount || 0);

        result.push({
          day: dayLabel,
          dateStr: item.dateStr,
          tempItems: [
            { value: total > 0 ? Math.round((item.normalCount || 0) / total * 100) : 0, class: 'bar-blue' },
            { value: total > 0 ? Math.round((item.abnormalCount || 0) / total * 100) : 0, class: 'bar-yellow' },
            { value: total > 0 ? Math.round((item.offlineCount || 0) / total * 100) : 0, class: 'bar-red' }
          ],
          humidityItems: [
            { value: 0, class: 'bar-cyan' },
            { value: 0, class: 'bar-yellow' },
            { value: 0, class: 'bar-red' }
          ]
        });
      });

      let limit = 7;
      if (this.trendRange === 'trend_range_15d') limit = 15;
      else if (this.trendRange === 'trend_range_30d') limit = 30;
      return result.slice(-limit);
    },
    mergeHumidityData(tempData, humData) {
      humData.forEach(humItem => {
        const tempItem = tempData.find(t => t.dateStr === humItem.dateStr);
        if (tempItem) {
          const total = (humItem.normalCount || 0) + (humItem.abnormalCount || 0) + (humItem.offlineCount || 0);
          tempItem.humidityItems = [
            { value: total > 0 ? Math.round((humItem.normalCount || 0) / total * 100) : 0, class: 'bar-cyan' },
            { value: total > 0 ? Math.round((humItem.abnormalCount || 0) / total * 100) : 0, class: 'bar-yellow' },
            { value: total > 0 ? Math.round((humItem.offlineCount || 0) / total * 100) : 0, class: 'bar-red' }
          ];
        }
      });
    },
    updateEquipmentRatioCharts(onlineCun, abnormalCun, offlineCun,) {
      const abnormalCount = abnormalCun;
      const offlineCount = offlineCun;
      const normalCount = onlineCun;

      this.abnormalRatioData = [
        { name: 'home.dashboard.normal', value: normalCount > 0 ? normalCount : 0, itemStyle: { color: '#10b981' } },
        { name: 'home.dashboard.abnormal', value: abnormalCount > 0 ? abnormalCount : 0, itemStyle: { color: '#f5a623' } }
      ];

      this.offlineRatioData = [
        { name: 'home.dashboard.normal', value: normalCount > 0 ? normalCount : 0, itemStyle: { color: '#10b981' } },
        { name: 'home.dashboard.offline', value: offlineCount > 0 ? offlineCount : 0, itemStyle: { color: '#e74c3c' } }
      ];

      this.applyRatioChartData();
    },
    applyRatioChartData() {
      if (this.abnormalRatioChart) {
        this.abnormalRatioChart.setOption({
          series: [{ data: this.abnormalRatioData.map(item => ({ ...item, name: this.$t(item.name) })) }]
        });
      }
      if (this.offlineRatioChart) {
        this.offlineRatioChart.setOption({
          series: [{ data: this.offlineRatioData.map(item => ({ ...item, name: this.$t(item.name) })) }]
        });
      }
    },
    formatAlertTime(timestamp) {
      if (!timestamp) return '';
      const now = Date.now();
      const diff = now - timestamp;

      if (diff < 60000) return this.$t('home.dashboard.just_now');
      if (diff < 3600000) return Math.floor(diff / 60000) + this.$t('home.dashboard.min_ago');
      if (diff < 86400000) return Math.floor(diff / 3600000) +  this.$t('home.dashboard.hour_ago');
      return Math.floor(diff / 86400000) +  this.$t('home.dashboard.day_ago');
    },
    getAlertBorderClass(index) {
      const classes = ['border-red', 'border-orange', 'border-blue'];
      return classes[index % classes.length];
    },
    loadTrendDataByRange() {
      this.loadEnvironmentalTrends(this.selectCustomer);
    },
    updateTime() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      this.currentTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    handleCustomerChange(val) {
      if (val) {
        this.loadDashboardData(val);
      }
    },
    // 查看轨迹
    handleViewTrack(carrierId, carrierName) {
      this.trackCarrierId = carrierId
      this.trackCarrierName = carrierName
      this.trackDialogVisible = true
    }
  },
  computed: {
    customerScope() {
      return this.$store.getters.userInfo.scope.customerScope
    },
    isProxy(){
      return this.$store.getters.userInfo.scope.proxy
    },
    isTrendStacked() {
      return this.trendRange === 'trend_range_15d' || this.trendRange === 'trend_range_30d';
    },

  },
  watch: {
    trendRange() {
      this.loadTrendDataByRange();
    },
    selectCustomer(newVal, oldVal) {
      if (newVal && oldVal && newVal !== oldVal) {
        this.loadCustomerCenter(newVal);
        this.loadDashboardData(newVal);
        this.clearMarkers(this.sceneMarkers);
        this.clearMarkers(this.assetMarkers);
        this.clearCustomerMarkers();
      }
    },
    '$i18n.locale'() {
      this.applyRatioChartData();
      this.updateMapLanguage();
    }
  }
};
</script>

<style lang="scss" scoped>
// ==================== Variables ====================
$color-blue-600: #2563eb;
$color-blue-500: #3b82f6;
$color-green-500: #10b981;
$color-green-600: #059669;
$color-yellow-500: #f59e0b;
$color-yellow-600: #d97706;
$color-orange-500: #f97316;
$color-red-500: #ef4444;
$color-red-600: #dc2626;
$color-cyan-500: #06b6d4;
$color-purple-500: #8b5cf6;

$text-gray-500: #6b7280;
$text-gray-600: #4b5563;
$text-gray-700: #374151;
$text-gray-800: #1f2937;

$bg-green-50: #ecfdf5;
$bg-yellow-50: #fffbeb;
$bg-red-50: #fef2f2;
$bg-orange-50: #fff7ed;
$bg-blue-50: #eff6ff;
$bg-cyan-50: #ecfeff;

$radius-sm: 8px;
$radius-md: 12px;
$radius-lg: 16px;

// ==================== Mixins ====================
@mixin card-shadow {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

@mixin flex-center {
  display: flex;
  align-items: center;
  justify-content: center;
}

@mixin text-colors {
  &.text-blue { color: $color-blue-600; }
  &.text-green { color: $color-green-600; }
  &.text-yellow { color: $color-yellow-600; }
  &.text-orange { color: $color-orange-500; }
  &.text-red { color: $color-red-600; }
  &.text-gray { color: $text-gray-800; }
}

// ==================== Base ====================
.iot-dashboard {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  font-family: 'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: $text-gray-800;
  line-height: 1.6;
}

.card-shadow {
  @include card-shadow;
}

// ==================== Top Bar ====================
.top-bar-row {
  margin-bottom: 24px;
}

.top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.customer-select-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.customer-label {
  font-size: 14px;
  color: $text-gray-600;
  font-weight: 500;
}

.customer-select {
  width: 180px;
  ::v-deep .el-input__inner {
    border-radius: $radius-sm;
    border-color: #e5e7eb;
    height: 36px;
    line-height: 36px;
    font-size: 14px;
    &:hover {
      border-color: $color-blue-500;
    }
    &:focus {
      border-color: $color-blue-600;
      box-shadow: 0 0 0 3px rgba($color-blue-600, 0.1);
    }
  }
}

.map-legend {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 6px 16px;
  border-radius: $radius-md;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: $text-gray-600;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;

  &.dot-green { background: $color-green-500; }
  &.dot-yellow { background: $color-yellow-500; }
  &.dot-red { background: $color-red-500; }
}

.header-live {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: $text-gray-600;
}

.header-bell-btn {
  background: transparent;
  border: none;
  color: $text-gray-600;

  &:hover {
    background: #f3f4f6;
    color: $text-gray-700;
  }
}

// ==================== Status Dots ====================
.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  animation: pulse 2s infinite;

  &.status-online { background: $color-green-500; }
  &.dot-green { background: $color-green-500; }
  &.dot-yellow { background: $color-yellow-500; }
  &.dot-orange { background: $color-orange-500; }
  &.dot-red { background: $color-red-500; }
  &.dot-blue { background: $color-blue-600; }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}

// ==================== Main ====================
.dashboard-main {
  padding: 24px;
  min-height: calc(100vh - 160px);
}

.main-inner {
  max-width: 80rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  height: 100%;
}

// ==================== Stats Cards ====================
.stats-row {
  margin-bottom: 24px;

  .el-col {
    margin-bottom: 0;
  }
}

.stat-card {
  background: #fff;
  border-radius: $radius-md;
  padding: 24px;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__label {
    font-size: 18px;
    color: $text-gray-500;
    margin: 0;
  }

  &__value {
    font-size: 28px;
    font-weight: 700;
    color: $text-gray-800;
    margin: 4px 0 0;
    @include text-colors;
  }

  &__icon {
    width: 50px;
    height: 50px;
    border-radius: $radius-sm;
    @include flex-center;

    i { font-size: 16px; }

    &.icon-bg-blue {
      background: $bg-blue-50;
      i { color: $color-blue-600; }
    }
    &.icon-bg-green {
      background: $bg-green-50;
      i { color: $color-green-600; }
    }
    &.icon-bg-orange {
      background: $bg-orange-50;
      i { color: $color-orange-500; }
    }
    &.icon-bg-red {
      background: $bg-red-50;
      i { color: $color-red-600; }
    }
  }
}

// ==================== Main Grid ====================
.main-grid {
  margin-bottom: 24px;
  display: flex;
  align-items: flex-start;
  min-height: 500px;

  ::v-deep .el-col {
    display: flex;
    flex-direction: column;
    min-height: 500px;
  }
}

// ==================== Map Card ====================
.map-card {
  background: #fff;
  border-radius: $radius-md;
  padding: 10px;
  flex-direction: column;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
  }

  &__title-group {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__title {
    font-size: 18px;
    font-weight: 600;
    color: $text-gray-800;
    margin: 0;
  }

  &__badge {
    padding: 4px 8px;
    background: $bg-blue-50;
    color: $color-blue-600;
    font-size: 12px;
    border-radius: 9999px;
  }

  &__legend {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f9fafb;
  padding: 8px 12px;
  border-radius: $radius-sm;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;

  &.dot-green { background: $color-green-500; }
  &.dot-yellow { background: $color-yellow-500; }
  &.dot-orange { background: $color-orange-500; }
  &.dot-red { background: $color-red-500; }
}

.legend-text {
  font-size: 12px;
  color: $text-gray-600;
}

// ==================== Customer Map Marker ====================
.customer-map-marker {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;

  &__dot {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease, background 0.2s ease;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    i {
      color: #1e57e8;
    }

    &:hover {
      transform: scale(1.15);
    }
  }

  &__label {
    margin-top: 4px;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    background: #fff;
    color: #333;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);

  }
}

// ==================== Map Area ====================
.map-area {
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 500px;
  background: linear-gradient(to bottom right, $bg-blue-50, #f3f4f6);
  border-radius: $radius-md;
  overflow: hidden;

  @media (min-width: 1024px) {
    min-height: 600px;
  }
  @media (min-width: 1280px) {
    min-height: 700px;
  }
}

// Tree Panel
.tree-panel {
  position: absolute;
  left: 0;
  top: 0;
  width: 300px;
  flex: 1;
  height: 100%;
  background: #fff;
  z-index: 50;
  display: flex;
  flex-direction: column;

  &__header {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 5px 10px;
  }

  &__close {
    color: $text-gray-500;
    padding: 4px;
    cursor: pointer;

    &:hover {
      color: $text-gray-700;
    }
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    padding: 8px;
    height: calc(100% - 35px);
    box-sizing: border-box;
  }
}

// Tree Panel Slide Transition
.tree-panel-slide-enter-active,
.tree-panel-slide-leave-active {
  transition: all 0.3s ease;
}

.tree-panel-slide-enter,
.tree-panel-slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

// Toggle Tree Button
.toggle-tree-btn {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 60px;
  background: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 100;
  border-radius: 0 4px 4px 0;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);

  i {
    color: #fff;
    font-size: 14px;
  }

  &:hover {
    filter: brightness(1.1);
    width: 28px;
  }
}

// Map Panel
.map-panel {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

@media (max-width: 1023px) {
  .map-area {
    height: 635px;
  }
  .tree-panel {
    width: calc(100% - 20px);
    max-height: 50%;
  }
  .toggle-tree-btn {
    left: 50%;
    top: 10px;
    transform: translateX(-50%);
    width: 60px;
    height: 24px;
    border-radius: 0 0 4px 4px;
  }
  .map-container {
    height: 400px;
  }
}

.map-container {
  width: 100%;
  height: 100%;
}

.map-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(239, 68, 68, 0.95);
  color: #fff;
  padding: 20px 24px;
  border-radius: 8px;
  text-align: center;
  z-index: 100;
  i {
    font-size: 24px;
    margin-right: 8px;
  }
}

.map-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(255, 255, 255, 0.95);
  padding: 20px 24px;
  border-radius: 8px;
  text-align: center;
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  i {
    font-size: 24px;
    color: $color-blue-600;
  }
  p {
    margin: 0;
    color: $text-gray-600;
  }
}

// 客户无定位提示样式
.map-location-tip {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%);
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 150;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);
  max-width: 90%;
  transition: left 0.3s ease;

  &--with-tree {
    left: calc(50% + 150px);
  }

  i {
    font-size: 20px;
    color: #f59e0b;
  }

  span {
    font-size: 14px;
    color: #92400e;
    font-weight: 500;
  }
}

// 提示淡入淡出动画
.location-tip-fade-enter-active {
  animation: tipFadeIn 0.3s ease-out;
}

.location-tip-fade-leave-active {
  animation: tipFadeOut 0.3s ease-in;
}

@keyframes tipFadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

@keyframes tipFadeOut {
  from {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  to {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
}

.asset-map-marker {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  &__dot {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.25);
    transition: transform 0.2s ease;
    i {
      font-size: 22px;
      color: #fff;
    }
    &:hover {
      transform: scale(1.15);
    }
  }
  &__label {
    margin-top: 4px;
    padding: 2px 8px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    color: #333;
    white-space: nowrap;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  }
}

.map-bg {
  position: absolute;
  inset: 0;
  opacity: 0.2;
  background-image: url('data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E');
}

.asset-marker-wrapper {
  position: absolute;
  cursor: pointer;
}

.asset-marker-dot {
  border-radius: 50%;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2);
  @include flex-center;

  i {
    color: #fff;
    font-size: 10px;
  }

  &.dot-green { background: $color-green-500; }
  &.dot-yellow { background: $color-yellow-500; }
  &.dot-red { background: $color-red-500; }
  &.dot-blue { background: $color-blue-600; }

  &.marker-size-md {
    width: 16px;
    height: 16px;
  }
  &.marker-size-lg {
    width: 24px;
    height: 24px;
  }
}

.asset-popup {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 4px;
  background: #fff;
  padding: 8px 12px;
  border-radius: $radius-sm;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
  z-index: 5;

  &__name {
    font-size: 14px;
    font-weight: 500;
    color: $text-gray-800;
    margin: 0;
  }

  &__detail {
    font-size: 12px;
    margin: 2px 0 0;
    @include text-colors;
  }
}

.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: opacity 0.3s ease;
}
.popup-fade-enter,
.popup-fade-leave-to {
  opacity: 0;
}

// ==================== Map Controls ====================
.map-controls {
  position: absolute;
  top: 16px;
  left: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #fff;
  padding: 8px;
  border-radius: $radius-sm;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.map-control-btn {
  border: none;
  color: $text-gray-600;

  &:hover {
    background: #f3f4f6;
    color: $text-gray-700;
  }
}

// ==================== Selected Asset ====================
.selected-asset {
  position: absolute;
  bottom: 16px;
  left: 16px;
  background: #fff;
  padding: 16px;
  border-radius: $radius-md;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2);
  width: 256px;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  &__label {
    font-size: 12px;
    color: $text-gray-500;
    margin: 0;
  }

  &__name {
    font-weight: 500;
    color: $text-gray-800;
    margin: 0;
  }

  &__metrics {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 12px;
  }

  &__btn {
    width: 100%;
    margin-top: 12px;
  }
}

.metric-box {
  padding: 8px;
  border-radius: $radius-sm;

  &--blue { background: $bg-blue-50; }
  &--cyan { background: $bg-cyan-50; }

  &__label {
    font-size: 12px;
    color: $text-gray-500;
    margin: 0;
  }

  &__value {
    font-size: 18px;
    font-weight: 700;
    margin: 0;

    .metric-box--blue & { color: $color-blue-600; }
    .metric-box--cyan & { color: $color-cyan-500; }
  }
}

// ==================== Right Column ====================
.right-column {
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-height: 500px;
}

.panel-card {
  background: #fff;
  border-radius: $radius-md;
  padding: 24px;
  padding-bottom: 32px;
  display: flex;
  flex-direction: column;

  &--flex {
    flex: 1;
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  &__title {
    font-size: 18px;
    font-weight: 600;
    padding-bottom: 10px;
    color: $text-gray-800;
    margin: 0;
  }

  &__link {
    font-size: 14px;
    color: $color-blue-600;
    text-decoration: none;

    &:hover { color: #1d4ed8; }
  }
}

// ==================== Equipment Ratio Charts ====================
.equipment-ratio-charts {
  display: flex;
  flex-direction: row;
  gap: 8px;
}

.equipment-ratio-chart-item {
  flex: 1;
  min-width: 0;
}

.chart-title {
  font-size: 12px;
  font-weight: 600;
  color: $text-gray-700;
  margin: 0 0 4px 0;
  text-align: center;
}

.equipment-ratio-chart {
  width: 100%;
  height: 150px;
}
// ==================== Device Status ====================
.device-status-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.device-status-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-radius: $radius-sm;

  &.bg-green-50 { background: $bg-green-50; }
  &.bg-yellow-50 { background: $bg-yellow-50; }
  &.bg-orange-50{ background: $bg-orange-50; }
  &.bg-red-50 { background: $bg-red-50; }
}

.device-status__left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.device-status__label {
  font-size: 14px;
  font-weight: 500;
  color: $text-gray-700;
}

.device-status__count {
  font-size: 18px;
  font-weight: 700;
  @include text-colors;
}

// ==================== Alerts ====================
.alerts-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.alert-item {
  padding: 12px;
  border-left: 4px solid;
  border-radius: 0 $radius-sm $radius-sm 0;

  &.border-red {
    border-left-color: $color-red-500;
    background: $bg-red-50;
  }
  &.border-yellow {
    border-left-color: $color-yellow-500;
    background: $bg-yellow-50;
  }
  &.border-orange {
    border-left-color: $color-orange-500;
    background: $bg-orange-50;
  }
  &.border-blue {
    border-left-color: $color-blue-500;
    background: $bg-blue-50;
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__title {
    font-size: 14px;
    font-weight: 500;
    color: $text-gray-800;
  }

  &__time {
    font-size: 12px;
    color: $text-gray-500;
  }

  &__desc {
    font-size: 12px;
    color: $text-gray-600;
    margin: 4px 0 0;
  }
}

// ==================== Trends ====================
.trends-card {
  background: #fff;
  border-radius: $radius-md;
  padding: 24px;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 24px;
  }

  &__title {
    font-size: 18px;
    font-weight: 600;
    color: $text-gray-800;
    margin: 0;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__select {
    width: 140px;
  }

  &__refresh {
    border: 1px solid #dcdfe6;
    color: $text-gray-600;

    &:hover {
      color: $color-blue-600;
      border-color: $color-blue-500;
    }
  }
}

.trend-row--stacked {
  .el-col {
    margin-bottom: 24px;
  }
}

.trend-block {
  &__title {
    font-size: 14px;
    font-weight: 500;
    color: $text-gray-600;
    margin: 0 0 16px;
  }
}

.chart-bars {
  height: 300px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding: 0 8px;
}

.chart-bar-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

.chart-bar {
  width: 100%;
  border-radius: $radius-sm $radius-sm 0 0;
  transition: height 0.6s ease-out;

  &.bar-blue { background: $color-blue-500; }
  &.bar-red { background: $color-red-500; }
  &.bar-yellow { background: $color-yellow-500; }
  &.bar-cyan { background: $color-cyan-500; }
  &.bar-purple { background: $color-purple-500; }
}

.chart-bars--stacked {
  .chart-bar-col {
    width: 100%;
  }
}

.chart-bar-stack {
  display: flex;
  flex-direction: column-reverse;
  width: 100%;
  height: 200px;

  .chart-bar {
    border-radius: 0;

    &:first-child {
      border-radius: 0 0 $radius-sm $radius-sm;
    }

    &:last-child {
      border-radius: $radius-sm $radius-sm 0 0;
    }
  }
}

.chart-bar-group {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 200px;
  width: 100%;
}

.chart-bar--grouped {
  flex: 1;
  border-radius: $radius-sm $radius-sm 0 0;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.85;
  }
}

.chart-tooltip {
  position: fixed;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 8px 12px;
  border-radius: $radius-sm;
  z-index: 1000;
  pointer-events: none;
  font-size: 13px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);

  &__day {
    font-weight: 600;
    margin-bottom: 4px;
    font-size: 14px;
  }

  &__content {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__status {
    color: rgba(255, 255, 255, 0.8);
    font-size: 12px;
  }

  &__value {
    font-weight: 600;
    font-size: 14px;
    color: #3b82f6;
  }
}

.chart-bar__label {
  font-size: 11px;
  color: $text-gray-500;
  margin-top: 8px;
  white-space: nowrap;
  transform: rotate(-30deg);
  transform-origin: top center;
}

.chart-legend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  margin-top: 16px;

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;

    &.dot-blue { background: $color-blue-500; }
    &.dot-yellow { background: $color-yellow-500; }
    &.dot-red { background: $color-red-500; }
  }

  &__text {
    font-size: 12px;
    color: $text-gray-500;
  }
}

.treemap-container {
  padding: 16px;
  height: 300px;
}

.treemap {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 12px;
}

.treemap-category {
  display: flex;
  flex-direction: column;
  background: #f9fafb;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.treemap-category-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #ffffff;
  border-bottom: 1px solid #f3f4f6;
}

.treemap-category-icon {
  font-size: 14px;
  color: #4b5563;
}

.treemap-category-name {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  flex: 1;
}

.treemap-category-value {
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
}

.treemap-items {
  display: flex;
  flex: 1;
  min-height: 40px;
}

.treemap-item {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease;
  cursor: pointer;
  padding: 4px;

  &:hover {
    opacity: 0.85;
  }

  &.treemap-normal {
    background: linear-gradient(135deg, #60a5fa, #3b82f6);
  }

  &.treemap-warning {
    background: linear-gradient(135deg, #fbbf24, #f59e0b);
  }

  &.treemap-alert {
    background: linear-gradient(135deg, #f87171, #ef4444);
  }
}

.treemap-item-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  text-align: center;
}

.treemap-item-name {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.treemap-item-value {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 600;
}

.treemap-legend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 8px 0;
}

.treemap-legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 12px;
  border-radius: 20px;
  transition: all 0.3s ease;
  opacity: 0.5;

  &--active {
    opacity: 1;
    background: rgba(0, 0, 0, 0.05);
  }

  &:hover {
    opacity: 0.8;
  }
}

.treemap-legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  transition: transform 0.3s ease;

  &--active {
    transform: scale(1.3);
  }

  &.treemap-normal {
    background: linear-gradient(135deg, #60a5fa, #3b82f6);
  }

  &.treemap-warning {
    background: linear-gradient(135deg, #fbbf24, #f59e0b);
  }

  &.treemap-alert {
    background: linear-gradient(135deg, #f87171, #ef4444);
  }
}

.treemap-legend-text {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.treemap-legend-count {
  font-size: 11px;
  color: #9ca3af;
  font-weight: 600;
  min-width: 30px;
  text-align: right;
}

// ==================== Asset Panel (for Tree) ====================
.asset-panel__content {
  flex: 1;
  overflow: hidden;

  ::v-deep .content {
    width: 100% !important;
    height: 100% !important;
    border: none !important;
    background: transparent !important;
  }

  ::v-deep .schema-tree {
    height: 100%;
    overflow-y: auto;
  }
}

// ==================== Responsive ====================
@media (max-width: 768px) {
  .header-inner {
    height: auto;
    padding: 12px 1rem;
    flex-wrap: wrap;
    gap: 8px;
  }
  .dashboard-main { padding: 16px; }
  .map-area { height: 550px; }
  .selected-asset {
    width: calc(100% - 32px);
    max-width: 256px;
  }
  .map-card__head { flex-direction: column; align-items: flex-start; }
  .trends-card__head { flex-direction: column; align-items: flex-start; }
}
</style>

<style lang="scss">
// ==================== Dynamic Map Marker Styles ====================
// 这些样式需要非 scoped 才能作用于动态创建的 DOM 元素
.marker-selected {
  .customer-map-marker__dot {
    i {
      color: #f97316 !important;
    }
    animation: marker-pulse 1.5s ease-in-out infinite;
  }
  .customer-map-marker__label {
    color: #f97316 !important;
  }
}

@keyframes marker-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

/* 弹窗样式 */
.custom-popup .mapboxgl-popup-content {
  padding: 0 !important;
  border-radius: 12px;
  min-width: 260px;
  max-width: 320px;
}

.popup-card {
  padding: 16px;
}

.popup-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.popup-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.popup-info {
  flex: 1;
}

.popup-title {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
}

.popup-transport {
  font-size: 12px;
  color: #10b981;
  margin-top: 2px;
}

.popup-stats {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
}

.popup-stat {
  flex: 1;
  text-align: center;
}

.popup-stat-value {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}

.popup-stat-label {
  display: block;
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}

.popup-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.popup-btn {
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.popup-btn.secondary {
  background: #f3f4f6;
  color: #374151;
}

.popup-btn.secondary:hover {
  background: #e5e7eb;
}

/* 取样值轮播样式 - 参考 temperature-monitor.html */
.popup-sample-carousel {
  position: relative;
  padding: 10px 0;
  border-top: 1px solid #e5e7eb;
  margin-top: 8px;
}

.sample-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background: rgba(30, 75, 168, 0.8);
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
  z-index: 10;
  opacity: 0.7;
}

.sample-nav-btn:hover {
  background: #1e4ba8;
  opacity: 1;
}

.sample-nav-prev {
  left: 0;
}

.sample-nav-next {
  right: 0;
}

.sample-slides-wrapper {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
}

.sample-slide {
  width: 100%;
  box-sizing: border-box;
}

/* 顶部状态栏 */
.sample-header-bar {
  display: flex;
  align-items: center;
  padding: 10px 12px 8px;
  background: #fff;
  position: relative;
}

.sample-header-bar .iconfont {
  color: #ff8a3d;
  font-size: 18px;
  flex-shrink: 0;
}

.sample-header-time {
  font-size: 12px;
  color: #333;
  font-weight: 500;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

/* 蓝色横条 - 区间显示 */
.sample-range-banner {
  background: #1e4ba8;
  color: #fff;
  text-align: center;
  padding: 8px 5px;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

/* 主值显示区 */
.sample-main-display {
  text-align: center;
  padding: 20px 12px 18px;
  background: #fff;
}

.sample-main-value {
  font-size: 20px;
  font-weight: 300;
  color: #1a1a1a;
  line-height: 1;
  letter-spacing: -1px;
}

.sample-main-unit {
  font-size: 20px;
  font-weight: 300;
  color: #1a1a1a;
  vertical-align: top;
  margin-left: 2px;
}

/* 记录列表 */
.sample-records {
  border-top: 1px solid #f0f0f0;
  background: #fff;
  max-height: 120px;
  overflow-y: auto;
}

.sample-record-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 5px;
  border-bottom: 1px solid #f0f0f0;
}

.sample-record-item:last-child {
  border-bottom: none;
}

.sample-record-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sample-device-icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7785;
  font-size: 14px;
}

.sample-device-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sample-device-id {
  font-size: 13px;
  color: #1a1a1a;
  font-weight: 500;
}

.sample-device-time {
  font-size: 11px;
  color: #9aa3ad;
}

.sample-record-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sample-temp-reading {
  font-size: 16px;
  color: #1a1a1a;
  font-weight: 500;
  min-width: 50px;
  text-align: right;
}

.sample-empty {
  text-align: center;
  color: #9ca3af;
  padding: 16px;
  font-size: 13px;
}

/* 查看轨迹按钮样式 */
.popup-btn.track-btn {
  background: linear-gradient(135deg, #4264fb, #1e57e8);
  color: #fff;
}

.popup-btn.track-btn:hover {
  background: linear-gradient(135deg, #5a7cfc, #3b6fe9);
}
</style>
